import { inject, injectable } from "inversify";
import { makeObservable, observable, computed, action, runInAction } from "mobx";
import type { UserType, UserWithOrgInstanceConfigs } from "@whatslly/types/User";
import { getRedirectUrl } from "@whatslly/commons/login-provider/getRedirectUrl";
import type { ILoginData, IUserStore } from "@whatslly/ui-components/services/UserStore";
import type { LoginProviderType } from "@whatslly/types/LoginProviderType";
import { openEtokenExpiredModal } from "@whatslly/ui-components/modals/EtokenExpiredModal";
import { getJSON, postJSON, putJSON, registerResponseInterceptor } from "@whatslly/commons/apiUtils";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { getConfigFromLoginProvider } from "@whatslly/commons/login-provider/getConfigFromLoginProvider";
import type { GetAppsTokenResponsType } from "@whatslly/types/integrations/auth/res/GetAppsTokenResponse";
import { parseOrReturnError } from "./utils";
import type { RefreshUserResponseType } from "@whatslly/types/RefreshUserResponse";
import { UrlStore } from "./urlStore";
import i18n from "../i18n/config";
import { modalService } from "@whatslly/ui-components/modals/ModalService";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { trackEvent, analyticsQueue } from "./analytics";
import { LoginCommands } from "@whatslly/commons/loginCommands";
import { loginListener } from "@whatslly/commons/loginListener";
import type { IStorage, StorageKey } from "@whatslly/commons/storage.constants";
import { InjectStorage, STORAGE } from "@whatslly/commons/storage.constants";
import { cleanStorage, saveResponseToStorage } from "@whatslly/commons/background/commands/commandUtils";
import { canUserLoginToTwilioSms, canUserLoginToWaba } from "@whatslly/commons/userMobileUtils";
import { UserPermissions } from "@whatslly/commons/granularPermissions";
import { MixpanelService } from "@whatslly/commons/mixpanel/MixpanelService";
import { OrgConfigs } from "@whatslly/types/OrgConfigs";
import type { PermissionInstanceConfig } from "@whatslly/commons/platformPermissions";
declare const window: any;

const POD_STORAGE_KEY = "tuvis:pod";
const LOGIN_PROVIDER_STORAGE_KEY = "tuvis:loginProvider";
const ETOKEN_STORAGE_KEY = "tuvis:etoken";
const USER_STORAGE_KEY = "tuvis:user";

@injectable()
export class UserStore implements IUserStore {
  initialized = false;
  refreshes = 0;
  private initializing = false;
  private _etoken?: string;
  private _loginProvider?: LoginProviderType;
  private _user?: UserType;
  private _pod = "us1";
  private _instanceConfigs: Pick<OrgConfigs, PermissionInstanceConfig> | null = null;
  get canUserLoginToTwilioSms() {
    return canUserLoginToTwilioSms(this.user);
  }

  get canUserLoginToWaba() {
    return canUserLoginToWaba(this.user);
  }

  get user(): UserWithOrgInstanceConfigs | undefined {
    if (!this._user) return;
    return observable(
      Object.assign({}, this._user, {
        permissions: this.contextAwarePermissions,
        permissionsInstanceConfigs: this.instanceConfigs ?? undefined,
      })
    );
  }

  set user(user: UserType | undefined) {
    this._user = user;
  }

  get etoken() {
    return this._etoken;
  }

  set etoken(etoken: string | undefined) {
    this._etoken = etoken;
  }

  get loginProvider() {
    return this._loginProvider;
  }

  set loginProvider(loginProvider: LoginProviderType | undefined) {
    this._loginProvider = loginProvider;
  }

  get pod() {
    const defaultPod = import.meta.env.VITE_API_DEFAULT_POD;
    return defaultPod === "st2" || defaultPod === "non-prod" ? defaultPod : this._pod;
  }

  set pod(newPod: string) {
    this._pod = newPod;
  }

  get isAnyAdmin() {
    return this._user?.isAdmin || this._user?.isSuperAdmin;
  }

  get isInternalUser() {
    return this.isAnyAdmin || this.isSupportUser;
  }

  get isSupportUser() {
    return this._user?.isSupportUser;
  }

  get isAdmin() {
    return this._user?.isAdmin;
  }

  get isSuperAdmin() {
    return this._user?.isSuperAdmin;
  }

  get hasAccountAdmin() {
    return this._user?.accounts?.some((account) => account.isAccountAdmin);
  }

  get hasUpdateAccessToOrg() {
    if (this.isAnyAdmin) return true;
    const currentOrgId = this.urlStore.searchParams?.get("orgId") ?? this._user?.organizationId;
    const isSameOrg =
      (this._user?.accounts && this._user?.accounts.length > 0 && this._user?.orgIds?.includes(currentOrgId ?? "")) ||
      currentOrgId === this._user?.organizationId;
    return isSameOrg;
  }

  get accounts() {
    return this._user?.accounts;
  }

  get hasAccessToMultipleOrgs() {
    return Boolean(
      this.isInternalUser === true || (this._user?.accounts && this._user.accounts.length > 0 && this._user.orgIds && this._user.orgIds?.length > 1)
    );
  }

  get contextAwarePermissions() {
    const currentOrgId = this.urlStore.searchParams?.get("orgId") ?? this._user?.organizationId;
    return this.permissions(currentOrgId);
  }

  permissions(orgId: string | undefined) {
    if (orgId && this._user) {
      if (orgId === this._user.organizationId) {
        return this._user?.permissions;
      } else if (this._user?.accounts && this._user?.accounts.length > 0 && this._user?.orgIds?.includes(orgId)) {
        return (
          this._user.accounts.flatMap((acccount) => acccount.permissions)?.find((perm) => perm.orgId === orgId)?.permission ??
          UserPermissions.none.toString()
        );
      }
    }

    return this._user?.permissions ?? UserPermissions.none.toString();
  }

  get instanceConfigs() {
    return this._instanceConfigs;
  }

  set instanceConfigs(orgConfigs: Pick<OrgConfigs, PermissionInstanceConfig> | null) {
    this._instanceConfigs = orgConfigs;
  }

  constructor(
    @inject(FrontLogger) private readonly logger: FrontLogger,
    @inject(UrlStore) private urlStore: UrlStore,
    @inject(InjectStorage) private readonly storage: IStorage,
    @inject(MixpanelService) private readonly mixpanelService: MixpanelService
  ) {
    makeObservable<UserStore, "_user" | "_loginProvider" | "_pod" | "_instanceConfigs">(this, {
      _user: observable,
      _loginProvider: observable,
      _pod: observable,
      _instanceConfigs: observable,
      initialized: observable,
      refreshes: observable,
      user: computed,
      isSuperAdmin: computed,
      isAnyAdmin: computed,
      instanceConfigs: computed,
      contextAwarePermissions: computed,
      updateUserData: action,
    });

    this.init();
    registerResponseInterceptor(async (response) => {
      if (response.status === 401 && this.etoken) {
        const loggedIn = await openEtokenExpiredModal(this);
        if (loggedIn === false) {
          this.logout();
        } else if (loggedIn) {
          await this.loadFromStorage();
          return { retry: { newEtoken: this.etoken } };
        }
      }
      if (response.headers.has("etoken") && response.headers.get("etoken") && this.etoken) {
        this.etoken = response.headers.get("etoken")!;
        await this.storage.set(STORAGE.ETOKEN_KEY, this.etoken);
      }
    });
  }

  private async loadFromStorage() {
    await Promise.all([
      this.migrateLocalStorage(POD_STORAGE_KEY, STORAGE.POD_KEY),
      this.migrateLocalStorage(LOGIN_PROVIDER_STORAGE_KEY, STORAGE.LOGIN_PROVIDER),
      this.migrateLocalStorage(ETOKEN_STORAGE_KEY, STORAGE.ETOKEN_KEY),
      this.migrateLocalStorage(USER_STORAGE_KEY, STORAGE.USER, true),
    ]);
    const { etoken, user, whatsllyPod, ...data } = await this.storage.get([
      STORAGE.ETOKEN_KEY,
      STORAGE.LOGIN_PROVIDER,
      STORAGE.POD_KEY,
      STORAGE.USER,
    ]);
    this.user = user;
    this._etoken = etoken;
    this._loginProvider = data[STORAGE.LOGIN_PROVIDER];
    this._pod = whatsllyPod ?? "us1";
  }

  private async init() {
    if (this.initialized || this.initializing) return;
    this.initializing = true;
    await this.loadFromStorage();
    this.mixpanelService.init({
      token: import.meta.env.VITE_MIXPANEL_TOKEN ?? "",
      user: this.user,
      logger: this.logger,
      source: "Platform",
      orgId: this.user?.organizationId ?? "",
    });
    runInAction(() => {
      this.initialized = true;
      this.initializing = false;
    });
  }

  private async migrateLocalStorage(oldKey: string, newKey: StorageKey, stringify = false) {
    const value = window.localStorage.getItem(oldKey);
    window.localStorage.removeItem(oldKey);
    try {
      if (value) await this.storage.set(newKey, stringify ? JSON.parse(value) : value);
    } catch {
      // do nothing
    }
  }

  async loginWithPopup({ url, width = 500, height = 600 }: { url: string; width?: number; height?: number }) {
    const top = (window.screen.availHeight - height) / 2;
    const left = (window.screen.availWidth - width) / 2;
    const options: Record<string, number | boolean> = {
      width,
      height,
      top: top > 0 ? top : 0,
      left: left > 0 ? left : 0,
      location: true,
      resizable: true,
      statusbar: true,
      toolbar: false,
    };

    const winOpt = Object.keys(options)
      .map((op) => `${op}=${Number(options[op])}`)
      .join(",");

    const popup = window.open(url, "Login", winOpt);
    const data = await new Promise<any>((resolve, reject) => {
      const interval = setInterval(() => {
        if (popup?.closed) {
          usubscribe();
          clearInterval(interval);
          reject(new Error(i18n.t("login.login_failed")));
        }
      }, 1000);
      const usubscribe = loginListener.on(LoginCommands.LOGIN_COMPLETED, (data) => {
        clearInterval(interval);
        resolve(data);
        usubscribe();
      });
    });
    const response = await postJSON({
      urlSuffix: "/integrations/auth/get-apps-token",
      pod: import.meta.env.VITE_API_DEFAULT_POD?.toString() || "us1",
      body: JSON.stringify(data),
    });
    const body: GetAppsTokenResponsType = await response.json();
    if (!body.success) {
      throw new Error(body.error ?? i18n.t("login.login_failed"));
    }

    if (body.data) {
      await saveResponseToStorage(body.data, body.appName);
      this.mixpanelService.trackLogin({
        userId: body.data.user._id,
        email: body.data.user.email,
        username: body.data.user.username,
        language: body.data.user.language,
        authSource: body.appName ?? "",
      });
    }

    await trackEvent({
      schemaName: "user_interaction",
      schemaContent: {
        interaction_name: "login-success",
      },
    });
    await analyticsQueue.flush();
    if (document.location.pathname.startsWith(`${import.meta.env.VITE_BASE_URL?.toString() ?? "/"}login`)) {
      document.location.href = `${import.meta.env.VITE_BASE_URL?.toString() ?? "/"}${location.search}`;
    }
  }

  async login({ email, customDomain, app }: ILoginData) {
    try {
      const redirectResponse = await getRedirectUrl({
        logger: this.logger,
        data: {
          domain: customDomain,
          email,
          type: app,
          chatApp: "popup-web",
          userPhone: "",
          loginSource: "platform.tuvis.com",
          inviteToken: this.urlStore.searchParams?.get("inviteToken") ?? undefined,
        },
      });
      const url = redirectResponse?.url;
      if (url) {
        await this.loginWithPopup({ url });
      }
    } catch (error: any) {
      await trackEvent({
        schemaName: "user_interaction",
        schemaContent: {
          interaction_name: "login-failed",
        },
      });
      throw new Error(error.message || i18n.t("login.login_failed"));
    }
  }

  async logout() {
    const { urlSuffix } = getConfigFromLoginProvider(this.loginProvider, "auth/logout");
    await Promise.all([
      getJSON({ etoken: this.etoken, urlSuffix }),
      trackEvent({ schemaName: "user_interaction", schemaContent: { interaction_name: "logout" } }),
      this.mixpanelService.trackLogout(),
      cleanStorage(),
    ]);
    this.user = undefined;
    this.etoken = undefined;
    this._loginProvider = undefined;
  }

  updateUserData(newUserData: Partial<UserType>) {
    this._user = { ...this.user, ...newUserData } as UserType;
  }

  async refreshUser() {
    const modal = modalService.openModal(Toast, {
      text: i18n.t("search_person.loading"),
      timeout: 60 * 5 * 1000,
    });
    try {
      const { urlSuffix } = getConfigFromLoginProvider(this.loginProvider, `auth/refresh`);
      const res = await putJSON({
        urlSuffix,
        body: JSON.stringify({ loginSource: "platform.tuvis.com" }),
        etoken: this.etoken ?? "",
      }).then((r) => parseOrReturnError<RefreshUserResponseType>(r, this.logger));
      if (!res.success) {
        return;
      }
      this.refreshes++;
      this.user = res.user;
      this.pod = res.wPod ?? "us1";
      await saveResponseToStorage(res);
      await trackEvent({
        schemaName: "user_interaction",
        schemaContent: {
          interaction_name: "refresh-user",
        },
      });
    } finally {
      modal.close(undefined);
    }
  }
}
